/**
 * Content configs for the /products/:slug landing pages.
 * Keyed by URL slug; consumed by src/pages/Products/index.jsx.
 */

const waitlist = { label: "Join the waitlist", action: "modal", modalKind: "waitlist" };
const contact = { label: "Talk to us", action: "modal", modalKind: "contact" };

export const PRODUCT_CONFIGS = {

    // ─────────── B2C ───────────

    "companion": {
        slug: "companion",
        segment: "b2c",
        eyebrow: "AI Companion",
        title: "AI Companion",
        tagline: "A Companion That Actually Remembers You",
        description:
            "An emotionally adaptive companion that learns your moods, picks up on your tone, and remembers what matters across every conversation. Not another chatbot — a presence.",
        seoDescription: "AIHumanity AI Companion — an emotionally adaptive companion that remembers your moods, voice, and history across every conversation.",
        stats: [
            { val: "Persistent", label: "memory" },
            { val: "Real-time", label: "emotion sensing" },
            { val: "Voice + Text", label: "multimodal" },
        ],
        primaryCta: waitlist,
        secondaryCta: { label: "Try the voice demo", href: "/demo/voice-agent", external: false },
        featuresHeading: "Built to feel like someone, not something",
        featuresIntro: "Three capabilities that turn a model into a relationship.",
        features: [
            {
                tag: "Continuity",
                title: "Persistent Memory",
                description: "Remembers your name, your goals, what you're worried about this week — and what you said three months ago. Memory you can read, edit, and own.",
                bullets: ["Episodic + semantic memory layers", "User-controlled memory editor", "Encrypted at rest"],
            },
            {
                tag: "Sensing",
                title: "Emotion-Aware Response",
                description: "Reads tone, facial expression, and pacing in real time. Softens when you're frustrated, energises when you're excited, listens when you go quiet.",
                bullets: ["Voice tone + facial expression fusion", "Adaptive pacing and prosody", "Calmness-aware reply length"],
            },
            {
                tag: "Voice",
                title: "Natural Voice Presence",
                description: "Low-latency voice with breath, laughter, hesitation. Sounds like a person who is actually thinking, not reading.",
                bullets: ["Sub-300 ms turn-taking", "Expressive prosody", "Cross-session voice consistency"],
            },
        ],
        useCases: [
            { icon: "💬", title: "Daily check-ins", body: "A morning conversation that picks up where yesterday left off." },
            { icon: "🌙", title: "Reflection partner", body: "Talk things through out loud — no judgement, full recall." },
            { icon: "🎧", title: "Hands-free companion", body: "Voice-first interaction while walking, driving, or cooking." },
            { icon: "🧠", title: "Personal context layer", body: "Plug into other AIHumanity products — they all share the same memory." },
        ],
        ctaHeading: "Meet your companion when it's ready",
        ctaBody: "Early access is rolling out in waves. Join the list to be invited.",
        ctaButton: waitlist,
    },

    "emotion-profile": {
        slug: "emotion-profile",
        segment: "b2c",
        eyebrow: "Emotion Profile Tracking",
        title: "Emotion Profile Tracking",
        tagline: "Your Emotional Patterns, Made Visible",
        description:
            "A personal dashboard of your emotional patterns across days, weeks, and months — built from voice, expression, and physiological signals. Private by default. Yours to read.",
        seoDescription: "AIHumanity Emotion Profile — track your emotional patterns over time using voice, expression, and biosignals. Private, on-device, yours to own.",
        stats: [
            { val: "Multi-signal", label: "voice · face · biosignal" },
            { val: "On-device", label: "private by default" },
            { val: "Longitudinal", label: "patterns over time" },
        ],
        primaryCta: waitlist,
        secondaryCta: { label: "See a sample profile", href: "/demo/emotion-profile", external: false },
        featuresHeading: "Patterns you can actually see",
        featuresIntro: "Not a single mood score — a multi-dimensional emotional fingerprint.",
        features: [
            {
                tag: "Tracking",
                title: "Daily Emotion Timeline",
                description: "A continuous emotional timeline of your day, drawn from short voice notes, video check-ins, or passive ambient sensing — your choice.",
                bullets: ["Voice, face, and biosignal inputs", "Configurable sampling cadence", "Annotate moments with context"],
            },
            {
                tag: "Insight",
                title: "Pattern Discovery",
                description: "Surface recurring patterns: how Mondays feel, what stress looks like before deadlines, when your calmest hours are.",
                bullets: ["Weekly + monthly trend digests", "Correlation with sleep, calendar, and weather (opt-in)", "Trigger detection — what precedes what"],
            },
            {
                tag: "Privacy",
                title: "Private by Architecture",
                description: "Signals can be processed on-device. You export, share, or delete your profile at will. No model is trained on your data without explicit consent.",
                bullets: ["Local-first storage option", "Granular export & deletion", "Optional anonymous aggregate research opt-in"],
            },
        ],
        useCases: [
            { icon: "🧘", title: "Self-awareness", body: "Notice the patterns you already feel but can't quite name." },
            { icon: "💼", title: "Work-life rhythm", body: "Map energy and stress against your real schedule." },
            { icon: "❤️", title: "Therapy support", body: "Bring richer data into sessions — with full consent and control." },
            { icon: "🌱", title: "Habit change", body: "See whether new habits actually move the emotional needle." },
        ],
        ctaHeading: "See your own emotional fingerprint",
        ctaBody: "Join the early-access list. We'll reach out when your profile is ready.",
        ctaButton: waitlist,
    },

    "mood-coach": {
        slug: "mood-coach",
        segment: "b2c",
        eyebrow: "Self-Help & Mood Coach",
        title: "Mood Coach",
        tagline: "A Coach That Actually Notices",
        description:
            "Guided emotional well-being that adapts in real time to how you actually feel — not just what you type. Evidence-informed exercises, delivered at the moment they matter.",
        seoDescription: "AIHumanity Mood Coach — adaptive, evidence-informed self-help that responds to your real-time emotional state via voice, expression, and biosignals.",
        stats: [
            { val: "Adaptive", label: "responds to your state" },
            { val: "Evidence-based", label: "CBT · ACT · mindfulness" },
            { val: "On-demand", label: "voice or text" },
        ],
        primaryCta: waitlist,
        secondaryCta: { label: "See Emotion Profile Tracking", href: "/products/emotion-profile", external: false },
        featuresHeading: "Coaching that meets you where you are",
        featuresIntro: "Three things every \"wellness app\" gets wrong — and what we built instead.",
        features: [
            {
                tag: "Adaptive",
                title: "State-Aware Sessions",
                description: "Senses your current state through voice and expression, then picks an exercise that fits — not a one-size-fits-all playlist.",
                bullets: ["Tone-aware exercise selection", "Real-time pacing adjustment", "Calmness-feedback loops"],
            },
            {
                tag: "Evidence",
                title: "Grounded Techniques",
                description: "CBT, ACT, mindfulness, and somatic exercises — adapted into short, voice-led sessions you'll actually finish.",
                bullets: ["3- to 7-minute guided sessions", "Reflection prompts with continuity", "Progress tracking that respects effort, not streaks"],
            },
            {
                tag: "Continuity",
                title: "Remembers Your Story",
                description: "Picks up where you left off. References last week's win. Knows which exercises landed and which didn't.",
                bullets: ["Cross-session memory", "Personal language and goals", "Optional sync with Emotion Profile Tracking data"],
            },
        ],
        useCases: [
            { icon: "😮‍💨", title: "Stress reset", body: "Two-minute resets at the moments stress actually spikes." },
            { icon: "🌅", title: "Morning grounding", body: "Start the day with a check-in that adapts to how you woke up." },
            { icon: "🛌", title: "Sleep wind-down", body: "Body-led breathing that responds to your real arousal level." },
            { icon: "💭", title: "Reframe in the moment", body: "Catch a thought spiral and walk it back, with guidance." },
        ],
        ctaHeading: "A coach that knows when to be gentle",
        ctaBody: "Mood Coach is in private beta. Join the list to be invited.",
        ctaButton: waitlist,
    },

    "text-humanizer": {
        slug: "text-humanizer",
        segment: "b2c",
        eyebrow: "Text Humanizer",
        title: "Text Humanizer",
        tagline: "Un-AI Your Writing",
        description:
            "Take AI-generated text and bring it back to a human voice — yours. Removes telltale AI rhythms, preserves meaning, and keeps your style intact across emails, posts, essays, and articles.",
        seoDescription: "AIHumanity Text Humanizer — rewrite AI-generated text to sound naturally human while preserving meaning and matching your personal voice.",
        stats: [
            { val: "Style-preserving", label: "matches your voice" },
            { val: "Detector-aware", label: "lower AI signal" },
            { val: "Meaning-safe", label: "no content drift" },
        ],
        primaryCta: waitlist,
        secondaryCta: { label: "See a before/after example", href: "/use-cases/authentic-writing", external: false },
        featuresHeading: "Sounds like you wrote it — because, fundamentally, you did",
        featuresIntro: "Three rewriting modes for different use cases.",
        features: [
            {
                tag: "Voice match",
                title: "Personal Style Profile",
                description: "Learn a writing style profile from a few samples of your own work — sentence rhythm, vocabulary, pacing, even quirks.",
                bullets: ["Builds a personal style fingerprint", "Editable tone sliders", "Multiple profiles for different contexts"],
            },
            {
                tag: "De-AI",
                title: "AI-Signal Reduction",
                description: "Targets the structural patterns that make text read as machine-written: uniform sentence length, over-hedging, telltale transitional phrases.",
                bullets: ["Cadence and rhythm rebalancing", "Hedge and filler reduction", "Detector-aware rewriting (with disclosure)"],
            },
            {
                tag: "Integrity",
                title: "Meaning Preservation",
                description: "Rewrites without rewriting what you meant. Built-in semantic check flags anywhere the meaning would shift before changes land.",
                bullets: ["Semantic diff before applying", "Citation and quote protection", "Side-by-side review mode"],
            },
        ],
        useCases: [
            { icon: "✉️", title: "Emails", body: "Sound like yourself in your inbox, not like a templated assistant." },
            { icon: "📝", title: "Articles & essays", body: "Long-form drafts that don't read as AI-generated." },
            { icon: "📣", title: "Social posts", body: "Match the voice of each platform — and yours." },
            { icon: "🎓", title: "Student writing", body: "For honest revision and learning — disclosure built in." },
        ],
        ctaHeading: "Write with AI. Read like a human.",
        ctaBody: "Join the early-access list — first 500 invited get a free style profile.",
        ctaButton: waitlist,
    },

    "voice-journal": {
        slug: "voice-journal",
        segment: "b2c",
        eyebrow: "Voice Journal",
        title: "Voice Journal",
        tagline: "Speak It. Get It Back as Insight.",
        description:
            "Talk into your phone for two minutes. Get back a transcript, an emotional read, and a short reflection you can keep. The fastest journaling that has ever existed.",
        seoDescription: "AIHumanity Voice Journal — a two-minute voice-first journal that returns transcript, emotional analysis, and reflective insight.",
        stats: [
            { val: "2 minutes", label: "to a complete entry" },
            { val: "Voice-first", label: "no typing required" },
            { val: "Emotional read", label: "tone + content fused" },
        ],
        primaryCta: waitlist,
        secondaryCta: { label: "Try voice demo", href: "/demo/voice-agent", external: false },
        featuresHeading: "Journaling that doesn't feel like work",
        features: [
            {
                tag: "Speak",
                title: "Two-Minute Entries",
                description: "Open the app, talk, close it. The entry — with insight — is waiting when you come back.",
                bullets: ["Background processing", "No prompts required", "Optional guided prompts when you want them"],
            },
            {
                tag: "Reflect",
                title: "Emotional Read",
                description: "Each entry comes with an emotional fingerprint drawn from tone, pacing, and content — together, not just text.",
                bullets: ["Tone + content fusion (not just sentiment)", "Themes surfaced across entries", "Pairs with Emotion Profile Tracking"],
            },
            {
                tag: "Insight",
                title: "Weekly Reflections",
                description: "On Sundays, a one-page reflection of your week: what you talked about, how it felt, what shifted.",
                bullets: ["Weekly summaries", "Theme and trigger tracking", "Exportable to PDF or therapist"],
            },
        ],
        useCases: [
            { icon: "🚶", title: "On a walk", body: "Voice journal while you move — no screen needed." },
            { icon: "🛏️", title: "Before bed", body: "Two minutes of out-loud reflection beats thirty of typing." },
            { icon: "🚗", title: "Post-meeting decompress", body: "Process in the car before the next thing starts." },
            { icon: "🧘", title: "Therapy prep", body: "Bring richer week-context into your session." },
        ],
        ctaHeading: "Journaling, finally, without the friction",
        ctaBody: "Voice Journal is rolling out in waves. Get on the list.",
        ctaButton: waitlist,
    },

    // ─────────── B2B ───────────

    "emotion-models": {
        slug: "emotion-models",
        segment: "b2b",
        eyebrow: "Emotion Models",
        title: "Emotion Models",
        tagline: "Pre-trained Emotion Recognition Across Face, Voice, and Text",
        description:
            "Production-ready emotion models for face, voice, and text — benchmarked, calibrated, and ready to drop into your stack. ONNX, TFLite, and server-side variants.",
        seoDescription: "AIHumanity Emotion Models — production-ready face, voice, and text emotion recognition models in ONNX, TFLite, and server variants.",
        stats: [
            { val: "3 modalities", label: "face · voice · text" },
            { val: "ONNX + TFLite", label: "edge + server" },
            { val: "Benchmarked", label: "open evaluation set" },
        ],
        primaryCta: { label: "Get model access", href: "https://developer.aihumanity.io", external: true },
        secondaryCta: contact,
        proof: {
            eyebrow: "Proof Block",
            title: "What ships is measurable and inspectable",
            body: "Not just an abstract emotion score. The model output is structured enough to audit, calibrate, and wire into downstream policy.",
            items: [
                { label: "Live FER demo", value: "Live browser demo available", detail: "Try real-time facial-expression capture in the Voice Agent demo on this site." },
                { label: "Output schema", value: "emotion + confidence + valence/arousal + AUs", detail: "Single forward pass returns both category and dimensional affect." },
                { label: "Deployment shape", value: "edge + server variants", detail: "The same label space carries across ONNX, TFLite, and hosted inference." },
            ],
            preview: {
                eyebrow: "Example response shape",
                title: "Sample model output",
                language: "JSON",
                code: `{
  "emotion": "joy",
  "confidence": 0.93,
  "valence": 0.71,
  "arousal": 0.38,
  "action_units": {
    "AU6": 0.82,
    "AU12": 0.91
  }
}`,
            },
        },
        featuresHeading: "Three modalities. One coherent label space.",
        featuresIntro: "Designed to be fused — see also Multimodal Fusion for the joint model.",
        features: [
            {
                tag: "Face",
                title: "Facial Expression Recognition",
                description: "Lightweight FER model trained on a curated multi-source dataset. Returns categorical, dimensional (valence/arousal), and AU outputs from a single forward pass.",
                bullets: ["Mobile-friendly ONNX + TFLite", "AU + categorical + valence/arousal", "Robust across skin tones and lighting"],
            },
            {
                tag: "Voice",
                title: "Speech Emotion Recognition",
                description: "Acoustic + prosodic features feed a transformer head that outputs the same emotion label space as the FER model — built for downstream fusion.",
                bullets: ["Low-latency streaming inference", "Speaker-invariant features", "Coherent with face label space"],
            },
            {
                tag: "Text",
                title: "Text Emotion Recognition",
                description: "Distilled transformer for short conversational text. Calibrated probabilities, not just argmax — built for use inside a fusion pipeline.",
                bullets: ["Calibrated probabilities", "Conversational + formal modes", "Tiny variant for on-device chat"],
            },
        ],
        useCases: [
            { icon: "🎮", title: "Games", body: "NPC reactions tied to real player emotion." },
            { icon: "🤖", title: "Assistants", body: "Emotion-aware response generation." },
            { icon: "📞", title: "Contact centres", body: "Real-time sentiment for live calls." },
            { icon: "📱", title: "Consumer apps", body: "On-device emotion read for mobile features." },
        ],
        ctaHeading: "Build with emotion that actually generalises",
        ctaBody: "Request developer access — model cards, benchmarks, and integration guides included.",
        ctaButton: { label: "Get developer access", href: "https://developer.aihumanity.io", external: true },
    },

    "fusion": {
        slug: "fusion",
        segment: "b2b",
        eyebrow: "Multimodal Fusion",
        title: "Multimodal Fusion",
        tagline: "Face + Voice + Biosignal. Fused, Not Stitched.",
        description:
            "A learned fusion model that combines face, voice, and physiological signals into a single calibrated emotional state stream — outperforming any single modality, and any naive late-fusion baseline.",
        seoDescription: "AIHumanity Multimodal Fusion — learned face + voice + biosignal fusion producing calibrated emotional state outputs.",
        stats: [
            { val: "Learned fusion", label: "not just averaging" },
            { val: "3+ modalities", label: "face · voice · biosignal" },
            { val: "Calibrated", label: "probabilities, not labels" },
        ],
        primaryCta: { label: "Get SDK access", href: "https://developer.aihumanity.io", external: true },
        secondaryCta: contact,
        proof: {
            eyebrow: "Proof Block",
            title: "The stream is built for downstream decisions",
            body: "Fusion exposes the signals your product layer needs: final state, uncertainty, modality presence, and a timestamped event stream you can replay.",
            items: [
                { label: "Transport", value: "single WebSocket event stream", detail: "One stream carries the fused state plus per-modality presence." },
                { label: "Uncertainty", value: "confidence + entropy exposed", detail: "Policies can branch on certainty instead of pretending every label is equally reliable." },
                { label: "Fallback behavior", value: "missing modalities handled gracefully", detail: "The stream remains usable when one channel drops or is unavailable." },
            ],
            preview: {
                eyebrow: "Example event",
                title: "Fused state payload",
                language: "JSON",
                code: `{
  "ts": "2026-07-08T10:14:22.182Z",
  "state": "calm",
  "confidence": 0.81,
  "entropy": 0.34,
  "modalities": ["face", "voice", "biosignal"],
  "scores": {
    "calm": 0.81,
    "stressed": 0.11,
    "joy": 0.08
  }
}`,
            },
        },
        featuresHeading: "Why one modality is never enough",
        featuresIntro: "Real emotion lives across the signals — fusion is where it actually shows up.",
        features: [
            {
                tag: "Architecture",
                title: "Learned Cross-Modal Fusion",
                description: "A fusion head trained on time-aligned multi-modal data, not a weighted average of independent classifiers. Handles missing modalities gracefully.",
                bullets: ["Time-aligned training data", "Robust to missing channels", "Joint embedding output"],
            },
            {
                tag: "Output",
                title: "Calibrated State Stream",
                description: "Streams calibrated probability distributions, not single labels — so downstream policies can reason about confidence, not just guess.",
                bullets: ["Temperature-calibrated outputs", "Configurable smoothing window", "Confidence and entropy exposed"],
            },
            {
                tag: "Integration",
                title: "Drop-in with Single Models",
                description: "Pairs natively with the single-modality Emotion Models — use the same label space, same input format, same calibration.",
                bullets: ["Shared label space across products", "WebSocket + native callback APIs", "Replay-friendly event log"],
            },
        ],
        useCases: [
            { icon: "🎯", title: "High-stakes detection", body: "Where a single noisy channel isn't enough." },
            { icon: "🤖", title: "Robotics", body: "Embodied agents need redundant emotional sensing." },
            { icon: "🩺", title: "Health & wellness", body: "Calmness and stress reads that survive real-world noise." },
            { icon: "🎮", title: "Premium NPCs", body: "Characters that read both face and voice." },
        ],
        ctaHeading: "Fuse the signals. Stop stitching them.",
        ctaBody: "Developer access includes the fusion model, single-modality models, and a unified streaming SDK.",
        ctaButton: { label: "Get developer access", href: "https://developer.aihumanity.io", external: true },
    },

    "session-insights": {
        slug: "session-insights",
        segment: "b2b",
        eyebrow: "Session Insights",
        title: "Session Insights",
        tagline: "Every Support Call, Interview, or Test — as a Structured Emotion Report",
        description:
            "Record any opt-in interaction — a support call, a user interview, a usability test — and get back a structured report of how the person actually felt: where frustration spiked, where trust built, and how the session ended. Built on the same detect · update · adapt models as our real-time products.",
        seoDescription: "AIHumanity Session Insights — turn any opt-in recorded interaction (support call, interview, usability test) into a structured post-session emotion report.",
        stats: [
            { val: "Any modality", label: "voice · video · text" },
            { val: "Structured report", label: "not a raw transcript" },
            { val: "Opt-in only", label: "consent-gated by design" },
        ],
        primaryCta: contact,
        secondaryCta: { label: "See a sample report", href: "/demo/voice-agent", external: false },
        proof: {
            eyebrow: "Proof Block",
            title: "The output is a report, not a raw signal dump",
            body: "Every session ends with a structured summary your team can act on — not a transcript someone still has to read end to end.",
            items: [
                { label: "Live sample", value: "Every call in the Voice Agent demo ends with one", detail: "Run the live demo on this site — the session report shown at the end is generated the same way." },
                { label: "Emotional arc", value: "opening vs. closing state, plus the dominant signal", detail: "Not a single sentiment score — a timeline of how the state moved across the session." },
                { label: "Key moments", value: "adaptation points flagged automatically", detail: "Turns where the detected state crossed a threshold are surfaced without manual tagging." },
            ],
            preview: {
                eyebrow: "Example output",
                title: "Sample session report",
                language: "JSON",
                code: `{
  "session_id": "call_9f21",
  "duration_s": 214,
  "dominant_emotion": "anger",
  "distribution": {
    "anger": 0.38,
    "neutral": 0.31,
    "happy": 0.19,
    "fear": 0.12
  },
  "opened": "neutral",
  "closed": "happy",
  "arc": "improving",
  "adaptations": 3
}`,
            },
        },
        featuresHeading: "From recording to something a team can use",
        featuresIntro: "Three steps: capture, analyze, deliver.",
        features: [
            {
                tag: "Capture",
                title: "Any Opt-In Session",
                description: "Point it at a support call, a moderated usability test, or a recorded interview — voice, video, or text transcripts all feed the same pipeline.",
                bullets: ["Voice, video, and text input", "Works alongside existing call/recording tools", "Consent and redaction built into the intake step"],
            },
            {
                tag: "Analyze",
                title: "Turns Raw Signal Into a Report",
                description: "The same face, voice, and text models — and the fusion engine — behind our real-time products run once, after the fact, across the whole session.",
                bullets: ["Emotional arc across the entire session", "Key moments flagged automatically", "Dominant emotion with a confidence score, not just polarity"],
            },
            {
                tag: "Deliver",
                title: "A Report Your Team Can Actually Use",
                description: "Structured output built for QA leads, researchers, and CX teams — not a data scientist's notebook.",
                bullets: ["Per-session report export", "Aggregate trends across many sessions", "Plugs into existing coaching or research workflows"],
            },
        ],
        useCases: [
            { icon: "🎧", title: "Support QA & coaching", body: "See which calls actually needed a supervisor, and why." },
            { icon: "🧑‍🔬", title: "User research", body: "An emotional read on every usability test, not just what testers said out loud." },
            { icon: "🗣️", title: "Customer interviews", body: "Know which parts of the pitch actually landed." },
            { icon: "🏢", title: "Enterprise CX programs", body: "Trend emotion across thousands of interactions, not just a CSAT score." },
        ],
        ctaHeading: "See what a session report actually looks like",
        ctaBody: "Try the live voice agent demo end to end, then talk to us about wiring this into your own call or research pipeline.",
        ctaButton: contact,
    },

    "custom-training": {
        slug: "custom-training",
        segment: "b2b",
        eyebrow: "Custom Model Training",
        title: "Custom Model Training",
        tagline: "Your Domain. Your Data. Our Models.",
        description:
            "Fine-tune our face, voice, text, and fusion models on your own data — privately. Domain-specific accuracy without giving up our calibration, label space, or model contracts.",
        seoDescription: "AIHumanity Custom Model Training — privately fine-tune emotion models on your domain data while keeping calibration and label-space contracts intact.",
        stats: [
            { val: "All modalities", label: "face · voice · text · fusion" },
            { val: "Private", label: "your data stays yours" },
            { val: "Contract-stable", label: "label space preserved" },
        ],
        primaryCta: contact,
        secondaryCta: { label: "See the base models", href: "/products/emotion-models", external: false },
        proof: {
            eyebrow: "Proof Block",
            title: "The output is a deliverable pack, not a black box",
            body: "Custom training should end in artifacts your team can review and operationalize, not just a better checkpoint with no context.",
            items: [
                { label: "Evaluation", value: "side-by-side baseline vs tuned report", detail: "Your team gets an eval pack tied to your own label policy and scenarios." },
                { label: "Integration impact", value: "no API contract change", detail: "The tuned model keeps the base label space so existing product code stays intact." },
                { label: "Operational handoff", value: "model card + drift plan + deletion attestations", detail: "Concrete documents ship with the tuned model, not as an afterthought." },
            ],
            preview: {
                eyebrow: "Sample handoff",
                title: "What a project closes with",
                language: "Deliverables",
                code: `model-card.pdf
baseline-vs-tuned-eval.csv
labeling-guidelines-review.md
deployment-checklist.md
drift-monitoring-plan.md
data-deletion-attestation.pdf`,
            },
        },
        featuresHeading: "Custom where it matters. Stable where it counts.",
        features: [
            {
                tag: "Process",
                title: "Guided Fine-Tuning",
                description: "We work with your team on data prep, labeling guidelines, training, evaluation, and deployment. You get a model card and full eval — not a black box.",
                bullets: ["Labeling guideline review", "Custom eval set with your stakeholders", "Drift monitoring after deployment"],
            },
            {
                tag: "Privacy",
                title: "Data Stays Where You Want It",
                description: "Training in your VPC, our SOC2-aligned environment, or air-gapped — your choice. Raw data never required to leave your control.",
                bullets: ["VPC / on-prem / air-gapped options", "Differential privacy available", "Data deletion attestations"],
            },
            {
                tag: "Contract",
                title: "Same API. Better Numbers.",
                description: "The fine-tuned model keeps the same label space and API as the base — your integration code doesn't change, only the accuracy on your domain does.",
                bullets: ["No SDK changes required", "Same calibration contract", "Side-by-side A/B harness"],
            },
        ],
        useCases: [
            { icon: "🏥", title: "Clinical research", body: "Domain-tuned models on consented patient data." },
            { icon: "🎮", title: "Game studios", body: "Tune to your characters, your players, your genre." },
            { icon: "🏢", title: "Enterprise CX", body: "Industry-specific emotional cues at scale." },
            { icon: "🤖", title: "Robotics OEMs", body: "Optimized for your sensor hardware and form factor." },
        ],
        ctaHeading: "Let's tune a model for your problem",
        ctaBody: "Send us a note. We'll schedule a scoping call and share a sample model card.",
        ctaButton: contact,
    },
};

export function getProductConfig(slug) {
    return PRODUCT_CONFIGS[slug] || null;
}
